import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import CreditCardAdd from './CreditCardAdd'
import VisaIcon from './assets/visa.svg?react'
import MastercardIcon from './assets/mastercard.svg?react'
import AmericanExpressIcon from './assets/american-express.svg?react'
import DiscoverIcon from './assets/discover.svg?react'
import SlideIcon from './assets/slide-icon.svg?react'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import { useTranslate } from '@repo/unicity'
import { SlideToAction } from '@repo/unicity/components/ui/navigation/slide-to-action'
import { useAtomValue } from 'jotai'
import { useNavigate } from 'react-router'
import { cartItemsAtom, cartTotalAtom, currencyAtom } from '@/core/store/cart.store'
import { customerDataAtom, seatingZoneAtom, editableCustomerAtom } from '@/core/store/customer.store'
import { creditCardState } from '@/core/store/creditcard.store'
import { formatPrice } from '../home'
import { useMemo, useRef, useState } from 'react'
import { processPayment, PaymentResponse, submitPaymentForm } from '@/core/services/payment.service'
import { get, has } from 'lodash'
import { customerHelpers } from '@helpers'
import { ticketOptionsAtom } from '@/core/store/ticket.store'
import { useTicketOptions } from '@/core/hooks/useTicketOptions'
import { isSupportedCardBrand } from '@/core/constants/payment'

// USHOP-2914: readable fallbacks for BE error keys not yet in the remote dictionary.
const ERROR_FALLBACKS: Record<string, string> = {
    glic_earlybird_ticket_already_purchased: 'This ID has already purchased an Early Bird ticket. Only 1 Early Bird ticket is allowed per ID.'
}

const Payment = () => {
    const navigate = useNavigate()
    const cartTotal = useAtomValue(cartTotalAtom)
    const cartItems = useAtomValue(cartItemsAtom)
    const customerData = useAtomValue(customerDataAtom)
    const editableCustomer = useAtomValue(editableCustomerAtom)
    const creditCard = useAtomValue(creditCardState)
    const currency = useAtomValue(currencyAtom)
    const seatingZone = useAtomValue(seatingZoneAtom)
    const ticketOptions = useAtomValue(ticketOptionsAtom)
    const { translate } = useTranslate()
    // translate() returns the raw key when missing — fall back to a readable message.
    const translateError = (key: string) => {
        const translated = translate(key)
        return translated === key ? (ERROR_FALLBACKS[key] ?? key) : translated
    }
    useTicketOptions() // Ensure ticket options are fetched
    const [isProcessing, setIsProcessing] = useState(false)
    const [paymentResponse, setPaymentResponse] = useState<PaymentResponse | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [errors, setErrors] = useState<string[]>([])
    const [resetSlider, setResetSlider] = useState(false)
    const formRef = useRef<HTMLFormElement>(null)

    // Check if personal info is complete (fullName, email, phone, seatingZone)
    const isPersonalInfoComplete = useMemo(() => {
        if (!editableCustomer) return false
        const { fullName, email, phone } = editableCustomer

        return fullName.length > 0 && email.length > 0 && phone.length > 0 && seatingZone.length > 0
    }, [editableCustomer, seatingZone])

    // Check if cart has items (total quantity > 0)
    const cartTotalQty = useMemo(() => {
        return cartItems.reduce((sum, item) => sum + item.quantity, 0)
    }, [cartItems])

    // Check if credit card data is complete
    const isCreditCardComplete = useMemo(() => {
        const { cardNumber, expDate, cvv, nameOnCard } = creditCard
        if (!cardNumber || !expDate || !cvv || !nameOnCard) return false
        // Check card number has at least 13 digits (min for valid card)
        if (cardNumber.replace(/\s/g, '').length < 13) return false
        // Check expDate format MM/YY or MM/YYYY
        const [month, year] = expDate.split('/')
        if (!month || !year || month.length !== 2) return false
        if (year.length !== 2 && year.length !== 4) return false
        // Check CVV has 3 or 4 digits
        if (cvv.length < 3) return false
        // Check name is not empty
        if (nameOnCard.trim().length === 0) return false
        // Only supported card brands (Visa/Mastercard) may proceed
        if (!isSupportedCardBrand(creditCard.cardType)) return false
        return true
    }, [creditCard])

    const handleSlideComplete = async () => {
        setIsProcessing(true)
        setError(null)
        setErrors([])

        try {
            // Validate credit card data
            if (!creditCard.cardNumber || !creditCard.expDate || !creditCard.cvv || !creditCard.nameOnCard) {
                throw new Error('Please fill in all credit card fields')
            }

            // Block unsupported card brands before hitting the payment gateway
            if (!isSupportedCardBrand(creditCard.cardType)) {
                throw new Error('card_brand_not_supported')
            }

            // Parse expiration date (MM/YY or MM/YYYY format)
            const [expMonth, expYearRaw] = creditCard.expDate.split('/')
            if (!expMonth || !expYearRaw) {
                throw new Error('Invalid expiration date')
            }
            // Convert YYYY to YY if needed
            const expYear = expYearRaw.length === 4 ? expYearRaw.slice(-2) : expYearRaw

            // Validate customer data
            if (!customerData || !editableCustomer) {
                throw new Error('Customer data not found')
            }

            // Prepare customer info using editable data
            const customerInfo = {
                distId: customerData.id?.unicity?.toString() || '',
                distName: editableCustomer.fullName,
                distStatus: customerHelpers.getCustomerStatus(customerData.status, customerData.type),
                distEmail: editableCustomer.email,
                distPhone: editableCustomer.phone,
                joinDate: customerData.joinDate,
                country: customerData.mainAddress?.country || 'US'
            }

            // Determine ticket type based on cart items
            const cartTicket = cartItems[0]?.ticket
            const selectedTicketOption = ticketOptions.find(t => t.item_code === cartTicket?.id)
            let ticketType = cartTicket?.category?.toLowerCase().replace(/\s+/g, '') || 'earlybird'

            // Check if customer is eligible for early bird pricing (for +1 bonus)
            // This applies when cart has Early Bird items and customer qualifies
            const hasEarlyBirdItems = cartItems.some(item => {
                const ticketOption = ticketOptions.find(t => t.item_code === item.ticket.id)
                return ticketOption?.ticket_type.toLowerCase().includes('early bird')
            })

            // Prepare ticket info from cart (filter out items with qty = 0)
            const tickets = cartItems
                .filter(item => item.quantity > 0)
                .map(item => ({
                    itemCode: item.ticket.id,
                    qty: item.quantity
                }))

            // Set ticketType to earlybird if cart has early bird items
            if (hasEarlyBirdItems) {
                ticketType = 'earlybird'
            }

            // Process payment
            // Only force success in development mode
            const isDev = false
            const response = await processPayment(
                {
                    cardNumber: creditCard.cardNumber.replace(/\s/g, ''),
                    expMonth: expMonth,
                    expYear: expYear,
                    cvv: creditCard.cvv,
                    cardholderName: creditCard.nameOnCard
                },
                customerInfo,
                tickets,
                seatingZone,
                ticketType,
                { isForcePaymentsSuccess: isDev }
            )

            // Only store payment response if successful
            if (response.success) {
                setPaymentResponse(response)

                // If we have payment_data with PAYMENT_URL, submit form to OCBC gateway
                if (response.payment_data?.PAYMENT_URL) {
                    // Give React time to update the form with new values
                    setTimeout(() => {
                        submitPaymentForm('frmPaymentOCBC')
                    }, 100)
                    return
                }

                // If payment completed directly (no OCBC redirect needed), go to success page
                if (response.reference_no) {
                    navigate(`/success/${response.reference_no}`)
                    return
                }
            }

            // If we reach here with response.success=false, treat as error
            if (!response.success) {
                if (response.errors && response.errors.length > 0) {
                    setErrors(response.errors)
                } else {
                    setError('Payment was not processed successfully')
                }
                setIsProcessing(false)
                setResetSlider(true)
                setTimeout(() => setResetSlider(false), 100)
                return
            }
        } catch (err) {
            if (has(err, 'response.data.error.bodyResponse.error.error_message')) {
                setError(get(err, 'response.data.error.bodyResponse.error.error_message'))
            } else {
                setError(err instanceof Error ? err.message : 'Payment failed')
            }
            setIsProcessing(false)
            // Reset slider on error
            setResetSlider(true)
            setTimeout(() => setResetSlider(false), 100)
        }
    }

    return (
        <>
            {/* Payment Processing Loader Modal */}
            <Modal
                open={isProcessing}
                aria-labelledby="payment-processing-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        zIndex: 1300
                    }}>
                    <Stack alignItems="center" gap={2}>
                        <CircularProgress size={60} sx={{ color: 'primary.main' }} />
                    </Stack>
                </Box>
            </Modal>

            <Stack
                p="48px 24px"
                sx={{
                    background: 'linear-gradient(180deg, #CCDEEF 0%, #FFFFFF 100%)',
                    flex: 1,
                    gap: 3
                }}>
                <Typography variant="h2" fontWeight={700} color="primary.main" textAlign="left" textTransform="uppercase">
                    {translate('payment_details')}
                </Typography>
                <Stack
                    sx={{
                        border: '2px solid rgba(153, 188, 223, 1)',
                        p: 3,
                        borderRadius: '8px',
                        bgcolor: 'background3.main'
                    }}>
                    <Stack gap={'12px'}>
                        <Typography variant="body1" color="primary.main" fontWeight={600}>
                            {translate('secure_checkout_with')}
                        </Typography>
                        <Stack direction={'row'} gap={1}>
                            <MastercardIcon />
                            <VisaIcon />
                        </Stack>
                    </Stack>
                    <Divider sx={{ borderColor: 'rgba(204, 222, 239, 1)', marginTop: '24px', marginBottom: '24px' }} />
                    <Stack direction={'column'}>
                        <CreditCardAdd />
                    </Stack>
                </Stack>
                <Stack
                    sx={{
                        bgcolor: 'background3.light',
                        boxSizing: 'border-box',
                        borderRadius: '5px',
                        justifyContent: 'space-between',
                        flexDirection: 'row',
                        alignItems: 'center',
                        p: '16px 24px'
                    }}>
                    <Typography variant="body2" color="primary.main" fontWeight={700}>
                        {translate('total_price')}
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="primary.main">
                        {formatPrice(cartTotal, currency)}
                    </Typography>
                </Stack>

                {/* Error Messages */}
                {error && (
                    <Alert severity="error" sx={{ bgcolor: 'rgba(255, 231, 235, 1)', justifyContent: 'center', width: '100%' }} icon={false}>
                        <Typography color="error" variant="body2" textAlign="center">
                            {translateError(error)}
                        </Typography>
                    </Alert>
                )}

                {errors.length > 0 && (
                    <Alert severity="error" sx={{ bgcolor: 'rgba(255, 231, 235, 1)', padding: '16px 24px', '.MuiAlert-message': { padding: '0px!important' }, borderRadius: '5px', justifyContent: 'center', width: '100%' }} icon={false}>
                        <Stack gap={0}>
                            {errors.map((err, index) => (
                                <Stack key={index} width={'100%'}>
                                    <Typography color="error" variant="body2" textAlign="center" >
                                        {translateError(err)}
                                    </Typography>
                                    {index < errors.length - 1 && <Divider sx={{ borderColor: 'rgba(244, 67, 54, 0.2)' }} />}
                                </Stack>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {/* Slide to Pay */}
                <Stack alignItems="center">
                    <SlideToAction
                        text={translate('slide_to_pay')}
                        onSlideComplete={handleSlideComplete}
                        disabled={isProcessing || !isCreditCardComplete || !isPersonalInfoComplete || cartTotalQty === 0}
                        reset={resetSlider}
                        icon={<SlideIcon />}
                        width={{ xs: '100%', sm: '100%' }}
                        height={60}
                        bgColor="rgba(0, 59, 111, 1)"
                        completedBgColor="rgba(111, 207, 151, 1)"
                    />
                </Stack>

                {/* Hidden OCBC Payment Form - All data comes from backend response */}
                <form ref={formRef} id="frmPaymentOCBC" method="post" action={paymentResponse?.payment_data?.PAYMENT_URL || ''} style={{ display: 'none' }}>
                    <input type="hidden" name="AMOUNT" value={paymentResponse?.payment_data?.AMOUNT || ''} />
                    <input type="hidden" name="MERCHANT_ACC_NO" value={paymentResponse?.payment_data?.MERCHANT_ACC_NO || ''} />
                    <input type="hidden" name="TRANSACTION_TYPE" value={paymentResponse?.payment_data?.TRANSACTION_TYPE || ''} />
                    <input type="hidden" name="MERCHANT_TRANID" value={paymentResponse?.payment_data?.MERCHANT_TRANID || ''} />
                    <input type="hidden" name="CARD_TYPE" value={paymentResponse?.payment_data?.CARD_TYPE || ''} />
                    <input type="hidden" name="RESPONSE_TYPE" value={paymentResponse?.payment_data?.RESPONSE_TYPE || ''} />
                    <input type="hidden" name="TXN_DESC" value={paymentResponse?.payment_data?.TXN_DESC || ''} />
                    <input type="hidden" name="TXN_SIGNATURE" value={paymentResponse?.payment_data?.TXN_SIGNATURE || ''} />
                    <input type="hidden" name="SIGNATURE_METHOD" value={paymentResponse?.payment_data?.SIGNATURE_METHOD || ''} />
                    <input type="hidden" name="RETURN_URL" value={paymentResponse?.payment_data?.RETURN_URL || ''} />
                    <input type="hidden" name="CARD_ISSUER_BANK_COUNTRY_CODE" value={paymentResponse?.payment_data?.CARD_ISSUER_BANK_COUNTRY_CODE || ''} />
                    <input type="hidden" name="CARD_NO" value={paymentResponse?.payment_data?.CARD_NO || ''} />
                    <input type="hidden" name="CARD_EXP_MM" value={paymentResponse?.payment_data?.CARD_EXP_MM || ''} />
                    <input type="hidden" name="CARD_EXP_YY" value={paymentResponse?.payment_data?.CARD_EXP_YY || ''} />
                    <input type="hidden" name="CARD_CVC" value={paymentResponse?.payment_data?.CARD_CVC || ''} />
                    <input type="hidden" name="CARD_HOLDER_NAME" value={paymentResponse?.payment_data?.CARD_HOLDER_NAME || ''} />
                    <input type="hidden" name="CUSTOMER_ID" value={paymentResponse?.payment_data?.CUSTOMER_ID || ''} />
                </form>
            </Stack>
        </>
    )
}

export default Payment
